import { getIdToken } from '$lib/stores/auth';
import type {
  User,
  Page,
  Card,
  CreateUserRequest,
  UpdateUserRequest,
  UpdatePageRequest,
  CreateCardRequest,
  UpdateCardRequest,
  CardsResponse,
  UploadResponse,
  ApiErrorResponse,
} from './types';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Add auth header if available
  const token = await getIdToken();
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorData = (await response.json()) as ApiErrorResponse;
    throw new ApiError(
      errorData.error?.code || 'UNKNOWN_ERROR',
      errorData.error?.message || 'An unknown error occurred',
      response.status
    );
  }

  return response.json() as Promise<T>;
}

async function requestFormData<T>(
  endpoint: string,
  formData: FormData
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;
  const headers: HeadersInit = {};

  // Add auth header if available
  const token = await getIdToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorData = (await response.json()) as ApiErrorResponse;
    throw new ApiError(
      errorData.error?.code || 'UNKNOWN_ERROR',
      errorData.error?.message || 'An unknown error occurred',
      response.status
    );
  }

  return response.json() as Promise<T>;
}

// API Client
export const api = {
  users: {
    get: (username: string) =>
      request<User>(`/users/${username}`),

    getMe: () =>
      request<User>('/users/me'),

    create: (data: CreateUserRequest) =>
      request<User>('/users', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (data: UpdateUserRequest) =>
      request<User>('/users/me', {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
  },

  pages: {
    get: (username: string) =>
      request<Page>(`/pages/${username}`),

    update: (data: UpdatePageRequest) =>
      request<Page>('/pages/me', {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
  },

  cards: {
    list: (username: string) =>
      request<CardsResponse>(`/pages/${username}/cards`),

    create: (data: CreateCardRequest) =>
      request<Card>('/cards', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: UpdateCardRequest) =>
      request<Card>(`/cards/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      request<{ success: boolean }>(`/cards/${id}`, {
        method: 'DELETE',
      }),
  },

  upload: {
    image: (file: File, type: 'icon' | 'tachie' | 'card') => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);
      return requestFormData<UploadResponse>('/upload', formData);
    },
  },
};

export { ApiError };
